import type { FC } from 'react';

import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';

import type { AssetsSegmentElasticSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type { MlPersonByAssetAndVersionNullable } from '@iconik-io/iconik-api/src/services/ml';
import { selectUserRoles } from '@iconik-io/shared-lib/src/redux/auth/selectors';
import { rangeToSmpteString } from '@iconik-io/shared-lib/src/utils/range_to_smpte_string';

import UserBubble from '../../../components/user_bubble/user_bubble';
import { selectAssetProxyTimecodeOptions } from '../../../ducks/pages/asset/selectors';
import {
  deletePersonSegment,
  type SetActivePersonSegmentOptions,
} from '../../../ducks/panels/asset_face_recognition/thunks';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import IconButton from '../../../ui/icon_button/icon_button';
import { getStatus } from '../utils';
import { useOnPersonSegmentClick } from './hooks/use_on_person_segment_click';
import { useScrollIntoView } from './hooks/use_scroll_into_view';

import styles from './recognized_persons.module.css';

type Props = {
  person: MlPersonByAssetAndVersionNullable;
  segments: AssetsSegmentElasticSchemaWithIdNullable[];
  highlightedSegmentIds: string[];
};

function hasDuration(segment: AssetsSegmentElasticSchemaWithIdNullable) {
  return (
    (segment?.time_end_milliseconds ?? 0) -
      (segment?.time_start_milliseconds ?? 0) !==
    0
  );
}

type SegmentsListItemProps = {
  segment: AssetsSegmentElasticSchemaWithIdNullable;
  person: MlPersonByAssetAndVersionNullable;
  isHighlighted: boolean;
  onSegmentClick: (
    segment: AssetsSegmentElasticSchemaWithIdNullable,
    options?: SetActivePersonSegmentOptions,
  ) => void;
};
const SegmentsListItem: FC<SegmentsListItemProps> = ({
  segment,
  person,
  isHighlighted,
  onSegmentClick,
}) => {
  const dispatch = useAppDispatch();
  const highlightRowRef = useScrollIntoView(Boolean(isHighlighted));
  const { id: assetId, versionId } = useParams({ strict: false });
  const userRoles = useAppSelector(selectUserRoles);
  const timecodeOptions = useAppSelector((state) =>
    selectAssetProxyTimecodeOptions(state, assetId, versionId),
  );

  const person_status = getStatus(person);

  return (
    <div
      ref={highlightRowRef}
      className={classNames(styles.person_segment_row, {
        [styles.highlighted]: isHighlighted,
      })}
    >
      <div className={styles.person_row_inner}>
        <UserBubble
          className={classNames({
            [styles.new_person_image]: person_status === 'NEW',
            [styles.confirmed_person_image]:
              person_status === 'USER_CONFIRMED' ||
              person_status === 'SYSTEM_CONFIRMED',
            [styles.unconfirmed_person_image]: person_status === 'UNCONFIRMED',
          })}
          size="sm"
          variant="square"
          name={person.name ?? ''}
          src={person.image_url ?? undefined}
        />
        <div className={styles.timecode_container}>
          <IconButton
            type="button"
            iconName="player_play"
            iconSizeUAD="2xs"
            data-segment-handler={segment.id}
            data-segment-start={segment?.time_start_milliseconds}
            data-segment-end={segment?.time_end_milliseconds}
            data-segment-play="true"
            onClick={() => onSegmentClick(segment, { shouldPlay: true })}
            disabled={!hasDuration(segment)}
          />
          <button
            type="button"
            className={styles.seek_button}
            data-segment-handler={segment.id}
            data-segment-start={segment?.time_start_milliseconds}
            data-segment-end={segment?.time_end_milliseconds}
            onClick={() => onSegmentClick(segment, { shouldPlay: false })}
          >
            {timecodeOptions
              ? rangeToSmpteString({
                  startMilliseconds: segment?.time_start_milliseconds,
                  endMilliseconds: hasDuration(segment)
                    ? segment?.time_end_milliseconds
                    : null,
                  timecodeOptions,
                })
              : null}
          </button>
        </div>
      </div>
      <IconButton
        variant="danger"
        onClick={() => {
          if (!userRoles.can_delete_person || !assetId) {
            return;
          }
          dispatch(deletePersonSegment(assetId, segment.id));
        }}
        disabled={!userRoles.can_delete_person}
        iconName="deletion_delete"
        aria-label="Remove recognized person"
      />
    </div>
  );
};

const SegmentsList: FC<Props> = ({
  person,
  segments,
  highlightedSegmentIds,
}) => {
  const handleSegmentClick = useOnPersonSegmentClick();
  return (
    <>
      {segments.map((segment) => {
        const isHighlighted = highlightedSegmentIds.includes(segment.id);
        return (
          <SegmentsListItem
            key={segment.id}
            segment={segment}
            person={person}
            isHighlighted={Boolean(isHighlighted)}
            onSegmentClick={handleSegmentClick}
          />
        );
      })}
    </>
  );
};

export default SegmentsList;
